// Formatting a deal's disclosed value.
//
// WHY THIS EXISTS. Deal values were formatted in six places with three
// different behaviours. Two of them hardcoded a euro sign, so a US deal read
// "€500M" — and 1,033 of the 1,271 valued deals in the platform are USD, against
// 129 that are actually EUR. The rest concatenated the raw ISO code onto the
// number and rendered "USD1.76B" and "EUR3B".
//
// NOTHING IS CONVERTED HERE. A deal is shown in the currency it was disclosed
// in. There is no FX source in this system, and inventing a rate would put a
// number in front of a user that no source ever stated. An INR deal reads in
// rupees because that is what was announced.

// Symbols that read unambiguously when pushed against a digit. Deliberately
// short: CAD, AUD, SGD, NZD and HKD all have a claim on "$", and CNY and JPY
// both on "¥", so those keep their code and a space rather than resolving to a
// symbol that would misattribute the currency.
const SYMBOL = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  KRW: '₩',
  ILS: '₪',
  PHP: '₱',
  VND: '₫',
  THB: '฿',
  NGN: '₦',
};

/** Trim a float for display: 1.76 stays, 3.0 becomes 3, 12.90 becomes 12.9. */
function trimNumber(n) {
  if (!Number.isFinite(n)) return null;
  return String(parseFloat(n.toFixed(2)));
}

/**
 * Format a `financials.dealValue` object for display.
 *
 * @param {{amount?: number|string, unit?: string, currency?: string}} dv
 * @returns {string|null} e.g. "$1.76B", "€3B", "SGD 179M" — or null when the
 *   platform holds no amount. Null means "not disclosed"; callers should render
 *   nothing or an em dash rather than a zero.
 */
export function formatDealValue(dv) {
  if (!dv) return null;
  const num = parseFloat(dv.amount);
  if (!Number.isFinite(num)) return null;

  // Some rows carry a bare "$" rather than an ISO code — an older extraction
  // path wrote the symbol straight through. Treat it as USD rather than
  // printing "$$396M".
  const raw = String(dv.currency || 'USD').trim().toUpperCase();
  const code = raw === '$' || raw === 'US$' ? 'USD' : raw;

  const unit = String(dv.unit || 'M').trim().toUpperCase();
  let n = num;
  let suffix = unit === 'B' ? 'B' : 'M';

  // Defensive, carried over from the dashboard formatter: an amount that
  // arrived in raw units rather than millions. No row in the platform is like
  // this today (0 of 1,271 valued deals), but the guard predates this helper and
  // dropping it would turn one bad row into "$500000000M" rather than "$500M".
  if (n >= 1_000_000_000) { n = n / 1_000_000_000; suffix = 'B'; }
  else if (n >= 1_000_000) { n = n / 1_000_000; suffix = 'M'; }

  // Millions reaching four figures read better as billions — "1.76B" rather
  // than "1760M", which is what the old formatters did too.
  if (suffix === 'M' && n >= 1000) {
    n = n / 1000;
    suffix = 'B';
  }

  const shown = trimNumber(n);
  if (shown === null) return null;

  const sym = SYMBOL[code];
  return sym ? `${sym}${shown}${suffix}` : `${code} ${shown}${suffix}`;
}

/**
 * Same, straight off a deal record. Falls back to the legacy flat fields some
 * older rows still carry, which have no currency attached — those are assumed
 * USD, the platform's majority currency, rather than the euro the previous code
 * assumed.
 */
export function formatDealValueFromDeal(deal) {
  if (!deal) return null;
  const fromFinancials = formatDealValue(deal.financials?.dealValue);
  if (fromFinancials) return fromFinancials;

  const legacy = deal.size ?? deal.value ?? deal.enterprise_value;
  if (legacy == null || legacy === '') return null;
  return formatDealValue({ amount: legacy, unit: 'M', currency: 'USD' });
}
